<!-- Auth modal - shown on demand when user tries a feature requiring login -->
<app-auth-modal
  *ngIf="showLoginModal"
  (authenticated)="onAuthenticated()">
</app-auth-modal>

<!-- Username picker - shown after login when user has no username set -->
<app-username-picker
  *ngIf="showUsernamePicker"
  (usernameSet)="onUsernameSet($event)">
</app-username-picker>


<ng-container *ngIf="(authState$ | async) as authState">
  <ng-container *ngIf="authState.initialized; else loadingScreen">
    <ng-container *ngIf="isDocsRoute; else editorLayout">
      <router-outlet></router-outlet>
    </ng-container>
  </ng-container>
  <ng-template #loadingScreen>
    <div class="app-loading" [attr.data-theme]="(currentTheme$ | async)">
      <div class="app-loading-logo"><span class="logo-pxl">pxl</span><span class="logo-vd">vd</span></div>
    </div>
  </ng-template>
</ng-container>

<ng-template #editorLayout>
<div class="main-container" [attr.data-theme]="currentTheme$ | async">

  <header class="pxl-header">
    <div class="pxl-header-left">
      <div class="pxl-logo">pxl<span class="logo-vd">vd</span><span class="logo-dot">.com</span></div>
      <div class="pxl-tagline">Pixel video studio</div>
    </div>
    <nav class="pxl-menubar">
      <app-file-menu #fileMenuRef (openProjectManagerRequested)="onOpenProjectManager()" (authRequired)="requireAuth()" (paymentRequired)="onPaymentRequired($event)" (fileImported)="onFileImported()" (saveRequested)="triggerSave()" (menuOpened)="collabPanel.closePanel()"></app-file-menu>
      <app-edit-menu></app-edit-menu>
      <button class="pxl-menu-btn" (click)="openSettings()" [disabled]="isSpectatorMode" [title]="isSpectatorMode ? 'Viewer mode: Settings are disabled' : 'Settings'">⚙ Settings</button>
      <button class="pxl-menu-btn" (click)="openClearData()">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
        Clear Data
      </button>
      <a class="pxl-menu-btn" href="/docs" target="_blank" rel="noopener">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
        Docs
      </a>
      <!-- Collab button - grand pill, lives right next to Docs -->
      <app-collab-panel #collabPanel [currentFrames]="collabFrames" [currentCels]="collabCels" [currentLayers]="collabLayers" [currentActiveLayerId]="collabActiveLayerId" (authRequired)="onCollabAuthRequired()" (paymentRequired)="onPaymentRequired($event)" (panelOpening)="onCollabPanelOpening()" class="collab-panel-menubar"></app-collab-panel>
    </nav>
    <div class="pxl-header-right">
      <!-- Home button - right side, left of sign-out -->
      <a class="pxl-menu-btn pxl-home-btn" href="/home" target="_blank" rel="noopener" title="Home">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9.5z"/><polyline points="9 21 9 12 15 12 15 21"/></svg>
        Home
      </a>
      <!-- User account pill -->
      <div class="auth-user-pill" *ngIf="auth.user">
        <span class="auth-user-at">@</span>
        <span class="auth-user-name">{{ collabSvc.localUser?.username ?? auth.getDisplayName() }}</span>
        <button class="auth-signout-btn" (click)="auth.signOut()" title="Sign out">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
        </button>
      </div>
      <div class="theme-pill">
        <button [class.active]="(currentTheme$ | async) === 'pxlvd'" (click)="setTheme('pxlvd')">pxlvd</button>
        <button [class.active]="(currentTheme$ | async) === 'dark'"  (click)="setTheme('dark')">Dark</button>
        <button [class.active]="(currentTheme$ | async) === 'light'" (click)="setTheme('light')">Light</button>
      </div>
    </div>
  </header>

  <div class="pxl-app-body" *ngIf="!showStartup"
    [style.grid-template-columns]="gridCols$ | async"
    (mousemove)="onGlobalMouseMove($event)"
    (mouseup)="onGlobalMouseUp()">

    <div class="pxl-panel-left">
      <app-tools (placeShape)="editorRef.placeExactShape($event)"></app-tools>
      <app-color-palette></app-color-palette>
    </div>

    <div class="col-resizer" (mousedown)="onColResizeStart($event, 'left')"></div>

    <div class="pxl-panel-center">
      <app-editor #editorRef></app-editor>
      <div class="row-resizer" (mousedown)="onRowResizeStart($event)"></div>
      <div class="frames-bottom" [style.height.px]="framesH">
        <app-frames-timeline></app-frames-timeline>
        <app-frames></app-frames>
      </div>
    </div>

    <div class="col-resizer" (mousedown)="onColResizeStart($event, 'right')"></div>

    <div class="pxl-panel-right">
      <app-right-side-bar (importFileSelected)="editorRef.triggerImageImport($event)"></app-right-side-bar>
    </div>
  </div>
</div>
<!-- Startup dialog - floats above canvas but below header -->
<app-startup-dialog *ngIf="showStartup"
  (confirmed)="handleStartup($event)"
  (importRequested)="handleStartupImport()">
</app-startup-dialog>

<!-- Save before collab dialog -->
<div class="save-collab-backdrop" *ngIf="showSaveBeforeCollab">
  <div class="save-collab-dialog">
    <div class="save-collab-icon">💾</div>
    <div class="save-collab-title">Save your work before collaborating?</div>
    <div class="save-collab-body">
      You have unsaved work on the canvas. Joining a collab room will replace your canvas with the room's shared canvas.
      Save your project now so you can come back to it later.
    </div>
    <div class="save-collab-actions">
      <button class="pxl-btn pxl-btn-primary" (click)="onSaveBeforeCollabConfirm()">
        💾 Save &amp; Continue
      </button>
      <button class="pxl-btn pxl-btn-secondary" (click)="onSaveBeforeCollabDiscard()">
        Continue without saving
      </button>
      <button class="pxl-btn pxl-btn-ghost" (click)="onSaveBeforeCollabCancel()">
        Cancel
      </button>
    </div>
  </div>
</div>

<!-- Resolution mismatch dialog -->
<div class="save-collab-backdrop" *ngIf="showResolutionMismatch && resMismatch">
  <div class="save-collab-dialog">
    <div class="save-collab-icon">📐</div>
    <div class="save-collab-title">Canvas Resolution Mismatch</div>
    <div class="save-collab-body">
      The room's canvas is <strong>{{ resMismatch.roomWidth }} × {{ resMismatch.roomHeight }}</strong>
      but your local canvas is <strong>{{ resMismatch.localWidth }} × {{ resMismatch.localHeight }}</strong>.
      <br><br>
      To collaborate properly, it's recommended to adopt the room's resolution. Otherwise, pixels may not align correctly between users.
    </div>
    <div class="save-collab-actions">
      <button class="pxl-btn pxl-btn-primary" (click)="onResolutionAdoptRoom()">
        📐 Adopt room resolution ({{ resMismatch.roomWidth }}×{{ resMismatch.roomHeight }})
      </button>
      <button class="pxl-btn pxl-btn-ghost" (click)="onResolutionKeepLocal()">
        Keep my resolution
      </button>
    </div>
  </div>
</div>

<!-- Clear data confirmation dialog -->
<div class="save-collab-backdrop" *ngIf="showClearDataConfirm">
  <div class="save-collab-dialog clear-data-dialog">
    <div class="save-collab-icon">🗑️</div>
    <div class="save-collab-title">Clear all locally stored data?</div>
    <div class="save-collab-body">
      This will permanently remove all projects, settings, and saved work stored in your browser.
      <br><br>
      <strong>This cannot be undone.</strong> You will also be signed out and the page will reload.
      Any work saved to your account in the cloud is not affected.
    </div>
    <div class="save-collab-actions">
      <button class="pxl-btn pxl-btn-danger" (click)="confirmClearData()">
        🗑️ Yes, clear everything
      </button>
      <button class="pxl-btn pxl-btn-ghost" (click)="showClearDataConfirm = false">
        Cancel
      </button>
    </div>
  </div>
</div>
<div class="save-toast" [class.visible]="saveToast">
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
  Saved
</div>

</ng-template>
